import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQuery({
    baseUrl: `${API_URL}/api`,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("hb_admin_token");
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: ["Video", "Gallery", "Construction", "PaymentPlan", "Document"],
  endpoints: (builder) => ({
    // ---- Public: Videos ----
    getVideos: builder.query({
      query: (type) => (type ? `/videos?type=${type}` : "/videos"),
      providesTags: ["Video"],
    }),
    createVideo: builder.mutation({
      query: (formData) => ({ url: "/videos", method: "POST", body: formData }),
      invalidatesTags: ["Video"],
    }),
    updateVideo: builder.mutation({
      query: ({ id, formData }) => ({ url: `/videos/${id}`, method: "PUT", body: formData }),
      invalidatesTags: ["Video"],
    }),
    deleteVideo: builder.mutation({
      query: (id) => ({ url: `/videos/${id}`, method: "DELETE" }),
      invalidatesTags: ["Video"],
    }),

    // ---- Public: Gallery ----
    getGallery: builder.query({
      query: () => "/gallery",
      providesTags: ["Gallery"],
    }),
    createGalleryImage: builder.mutation({
      query: (formData) => ({ url: "/gallery", method: "POST", body: formData }),
      invalidatesTags: ["Gallery"],
    }),
    updateGalleryImage: builder.mutation({
      query: ({ id, formData }) => ({ url: `/gallery/${id}`, method: "PUT", body: formData }),
      invalidatesTags: ["Gallery"],
    }),
    deleteGalleryImage: builder.mutation({
      query: (id) => ({ url: `/gallery/${id}`, method: "DELETE" }),
      invalidatesTags: ["Gallery"],
    }),

    // ---- Public: Construction Update ----
    getConstructionUpdate: builder.query({
      query: () => "/construction-update",
      providesTags: ["Construction"],
    }),
    updateConstructionUpdate: builder.mutation({
      query: (body) => ({ url: "/construction-update", method: "PUT", body }),
      invalidatesTags: ["Construction"],
    }),
    addConstructionPhoto: builder.mutation({
      query: (formData) => ({ url: "/construction-update/photos", method: "POST", body: formData }),
      invalidatesTags: ["Construction"],
    }),
    deleteConstructionPhoto: builder.mutation({
      query: (photoId) => ({ url: `/construction-update/photos/${photoId}`, method: "DELETE" }),
      invalidatesTags: ["Construction"],
    }),

    // ---- Public: Payment Plans ----
    getPaymentPlans: builder.query({
      query: () => "/payment-plans",
      providesTags: ["PaymentPlan"],
    }),
    getAllPaymentPlans: builder.query({
      query: () => "/payment-plans/all",
      providesTags: ["PaymentPlan"],
    }),
    createPaymentPlan: builder.mutation({
      query: (body) => ({ url: "/payment-plans", method: "POST", body }),
      invalidatesTags: ["PaymentPlan"],
    }),
    updatePaymentPlan: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/payment-plans/${id}`, method: "PUT", body }),
      invalidatesTags: ["PaymentPlan"],
    }),
    deletePaymentPlan: builder.mutation({
      query: (id) => ({ url: `/payment-plans/${id}`, method: "DELETE" }),
      invalidatesTags: ["PaymentPlan"],
    }),

    // ---- Public: Documents ----
    getDocument: builder.query({
      query: (key) => `/documents/${key}`,
      providesTags: (result, error, key) => [{ type: "Document", id: key }],
    }),
    getAllDocuments: builder.query({
      query: () => "/documents",
      providesTags: ["Document"],
    }),
    uploadDocument: builder.mutation({
      query: ({ key, formData }) => ({ url: `/documents/${key}`, method: "POST", body: formData }),
      invalidatesTags: (result, error, { key }) => [{ type: "Document", id: key }, "Document"],
    }),
  }),
});

export const {
  useGetVideosQuery,
  useCreateVideoMutation,
  useUpdateVideoMutation,
  useDeleteVideoMutation,
  useGetGalleryQuery,
  useCreateGalleryImageMutation,
  useUpdateGalleryImageMutation,
  useDeleteGalleryImageMutation,
  useGetConstructionUpdateQuery,
  useUpdateConstructionUpdateMutation,
  useAddConstructionPhotoMutation,
  useDeleteConstructionPhotoMutation,
  useGetPaymentPlansQuery,
  useGetAllPaymentPlansQuery,
  useCreatePaymentPlanMutation,
  useUpdatePaymentPlanMutation,
  useDeletePaymentPlanMutation,
  useGetDocumentQuery,
  useGetAllDocumentsQuery,
  useUploadDocumentMutation,
} = apiSlice;
